// Static, predefined operator-token helpers for Global Search (no AI, no API
// calls). `status`/`date`/`type` suggestion lists below are fixed enums meant
// purely as typing hints - actual filtering/validation of whatever value the
// user types happens server-side (globalSearch.js's operator parser); an
// invalid value just yields zero matching results, same as any other term.

export const OPERATOR_KEYS = [
  "employee",
  "department",
  "branch",
  "status",
  "date",
  "shift",
  "designation",
  "type",
  "month",
  "year",
];

// Only keys with a fixed, safe-to-predefine enum get static suggestions.
// Free-type keys (employee/department/branch/shift/designation/month/year)
// intentionally have no list - getOperatorValueSuggestions returns [] for them.
const STATIC_VALUE_SUGGESTIONS = {
  status: ["Pending", "Approved", "Rejected", "Cancelled", "Present", "Absent"],
  date: [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "this-week" },
    { label: "Last Week", value: "last-week" },
    { label: "This Month", value: "this-month" },
    { label: "Last Month", value: "last-month" },
  ],
  type: ["Leave", "Attendance", "Expense", "Ticket"],
};

const OPERATOR_KEYS_LOWER = OPERATOR_KEYS.map((key) => key.toLowerCase());

// Matches `key:value` or `key:"quoted value"` where key is one of
// OPERATOR_KEYS (case-insensitive). Value (unquoted) is any run of
// non-whitespace; quoted value is anything but a double quote.
const TOKEN_REGEX = /(\w+):(?:"([^"]+)"|(\S+))/g;

const collapseWhitespace = (str) => str.replace(/\s+/g, " ").trim();

/**
 * Scans `query` for recognized `key:value` tokens.
 * Returns { freeText, chips } where chips is [{ key, value, raw }] in the
 * order found, and freeText is the input with every matched token's `raw`
 * substring removed and whitespace collapsed. Unrecognized `key:` prefixes
 * (key not in OPERATOR_KEYS) are left untouched in freeText - same inert
 * passthrough rule the backend's operator parser uses.
 */
export const parseOperatorTokens = (query) => {
  const input = query || "";
  const chips = [];
  let freeText = input;

  // Dedupe by key+value (case-insensitive on the value too, same as a status
  // like "Pending"/"pending" meaning the same filter) - typing or selecting
  // the same key:value twice (e.g. via the operator-value autocomplete, which
  // can easily be invoked again for an already-applied token) must not
  // produce two identical chips.
  const seenChipKeys = new Set();

  let match = TOKEN_REGEX.exec(input);
  while (match !== null) {
    const [raw, rawKey, quotedValue, plainValue] = match;
    const key = rawKey.toLowerCase();
    if (OPERATOR_KEYS_LOWER.includes(key)) {
      const value = quotedValue !== undefined ? quotedValue : plainValue;
      if (value && value.length > 0) {
        const dedupeKey = `${key}:${value.toLowerCase()}`;
        if (!seenChipKeys.has(dedupeKey)) {
          seenChipKeys.add(dedupeKey);
          chips.push({ key, value, raw });
        }
        freeText = freeText.replace(raw, " ");
      }
    }
    match = TOKEN_REGEX.exec(input);
  }

  return { freeText: collapseWhitespace(freeText), chips };
};

/**
 * Removes exactly one operator token's `raw` substring from `query`, leaving
 * every other token and the free text untouched (aside from whitespace
 * collapse). `token` may be the chip object ({ raw, ... }) or a plain
 * { key, value } pair - when `raw` isn't given, it's reconstructed as
 * `key:value` to match what parseOperatorTokens would have produced.
 */
export const removeOperatorToken = (query, token) => {
  const input = query || "";
  if (!token) return collapseWhitespace(input);
  const raw = token.raw || `${token.key}:${token.value}`;
  const index = input.indexOf(raw);
  if (index === -1) return collapseWhitespace(input);
  const result = input.slice(0, index) + " " + input.slice(index + raw.length);
  const collapsed = collapseWhitespace(result);
  // Deliberately end with one trailing space (when anything remains) rather
  // than trimming it away. Removing one chip can leave an EARLIER, already-
  // complete token (e.g. "status:pending") sitting at the new end of the
  // string with no trailing space - and index.js's trailing-unfinished-token
  // regex (`/(\w+):([^\s]*)$/`) can't tell that apart from the user actively
  // mid-typing that same token, so it wrongly reopens the operator-value
  // autocomplete popup for a token nobody is editing. A single trailing
  // space breaks that regex's `$`-anchored match (it requires a non-space
  // run right up to the end of the string) without being visible in the
  // input, so the stale popup no longer resurfaces after a chip removal.
  return collapsed ? `${collapsed} ` : collapsed;
};

/**
 * Returns the static suggestion list for `key` filtered by a case-insensitive
 * substring match on `partialValue`. [] when the key has no static list
 * (free-type keys) or isn't recognized at all.
 */
export const getOperatorValueSuggestions = (key, partialValue) => {
  if (!key) return [];
  const list = STATIC_VALUE_SUGGESTIONS[key.toLowerCase()];
  if (!list) return [];
  const needle = (partialValue || "").toLowerCase();
  if (!needle) return list;
  return list.filter((entry) => {
    const label = typeof entry === "string" ? entry : entry.label;
    const value = typeof entry === "string" ? entry : entry.value;
    return label.toLowerCase().includes(needle) || value.toLowerCase().includes(needle);
  });
};
